import React from 'react'
import { useLogic } from '~common/ui-logic/hooks/logic'
import styled from 'styled-components'
import { Icon } from '~common/components/icon'
import Modal from '~common/components/modal'
import { LoadingIndicator } from '~common/components/loading'
import { type ExportButtonDependencies, ExportButtonLogic } from './logic'
import { Button } from '~common/components/button'
import DropdownMenu from '~common/components/dropdown-menu'
import { useAppContext } from '~/app-context'

export default function ExportButton(props: ExportButtonDependencies) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(ExportButtonLogic, {
        ...props,
        ...appContext,
    })

    const dropdownItems = [
        {
            id: 'invoice-files',
            label: 'Invoice Files',
            icon: 'gdrive',
            onClick: () => logic.selectInvoiceFiles(),
        },
        {
            id: 'csv',
            label: 'Metadata CSV',
            icon: 'gsheets',
            onClick: () => logic.selectCsvFiles(),
        },
    ]

    return (
        <>
            <DropdownMenu
                trigger={
                    <Button
                        icon="arrow-right"
                        iconPosition="right"
                        label={props.label}
                        variant={props.variant || 'ghost'}
                        size="medium"
                        maxWidth="150px"
                        fullWidth
                    />
                }
                items={dropdownItems}
                placement="bottom"
            />

            <Modal
                isOpen={logic.state.isExportModalOpen}
                onClose={() => logic.closeExportModal()}
                size="small"
                showCloseButton={false}
                closeOnOverlayClick={false}
                closeOnEscape={false}
            >
                <ModalContent>
                    {logic.state.exportState === 'idle' && (
                        <>
                            <ModalTitle>Select Export Type</ModalTitle>
                            <SelectionButtons>
                                <Button
                                    onClick={() =>
                                        logic.selectBankTransactions()
                                    }
                                    label="Bank Transactions"
                                    variant="outline"
                                    fullWidth
                                />
                                <Button
                                    onClick={() => logic.selectInvoices()}
                                    label="Invoices"
                                    variant="outline"
                                    fullWidth
                                />
                            </SelectionButtons>
                            <Button
                                onClick={() => logic.closeExportModal()}
                                label="Cancel"
                                variant="ghost"
                            />
                        </>
                    )}

                    {logic.state.exportState === 'exporting' && (
                        <>
                            <LoadingIndicator size="large" />
                            <ModalTitle>Exporting to Google Sheets</ModalTitle>
                            <ModalDescription>
                                Please wait while we export your invoice data to
                                Google Sheets...
                            </ModalDescription>
                        </>
                    )}

                    {logic.state.exportState === 'success' && (
                        <>
                            <SuccessIcon>
                                <Icon name="check" size="48" />
                            </SuccessIcon>
                            <ModalTitle>Export Complete!</ModalTitle>
                            <ModalDescription>
                                Your invoice data has been successfully exported
                                to Google Sheets.
                            </ModalDescription>
                            <ModalActions>
                                <Button
                                    onClick={() =>
                                        logic.openExportedSpreadsheet()
                                    }
                                    label="Open Spreadsheet"
                                />
                                <Button
                                    onClick={() => logic.closeExportModal()}
                                    label="Close"
                                />
                            </ModalActions>
                        </>
                    )}

                    {logic.state.exportState === 'error' && (
                        <>
                            <ErrorIcon>
                                <Icon name="close" size="48" />
                            </ErrorIcon>
                            <ModalTitle>Export Failed</ModalTitle>
                            <ModalDescription>
                                {logic.state.exportError ||
                                    'An error occurred during export.'}
                            </ModalDescription>
                            <ModalActions>
                                <Button
                                    onClick={() => logic.closeExportModal()}
                                    label="Close"
                                />
                            </ModalActions>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    )
}

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${({ theme }) => theme.spacing[4]};
    min-height: 200px;
    justify-content: center;
`

const ModalTitle = styled.h3`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const ModalDescription = styled.p`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`

const SuccessIcon = styled.div`
    color: ${({ theme }) => theme.colors.success};
`

const ErrorIcon = styled.div`
    color: ${({ theme }) => theme.colors.error};
`

const SelectionButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
    width: 100%;
    margin: ${({ theme }) => theme.spacing[4]} 0;
`

const ModalActions = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[3]};
    margin-top: ${({ theme }) => theme.spacing[4]};
    justify-content: center;
`
